<template>
  <div class="dropdown-search" ref="dropdown">
    <Input
      :inputValue.sync="searchText"
      class="dropdown-input"
      ref="input"
      :placeholder="placeholder"
      :textAlign="textAlign"
      :isDisabled="isDisabled"
      @focusChanged="inputFocusChanged"
      @inputChanged="searchTextInputChanged"
      @keydown.native="inputKeyDown"
    />

    <div class="arrow-container" @mousedown="arrowClicked">
      <div :class="['arrow-down', { 'arrow-flip': dropdownIsVisible, 'arrow-disabled': isDisabled }]"></div>
    </div>

    <div v-show="dropdownIsVisible" class="dropdown-search-list" ref="dropdownList">
      <div class="filtered-caption">
        <div>{{ caption }}</div>
        <img
          v-if="searchText"
          title="Clear search"
          src="https://dsmapi.logistics.systems/dsm6gIcons/Clear%20Filter.png"
          height="15px"
          @mousedown="clearSearch"
        />
      </div>

<div v-if="canSearch || showDefaultLocalResults">
        <div v-if="searching" class="status-row">Searching...</div>
        <div v-else-if="searchError" class="status-row item-error">{{ searchError }}</div>
        <div v-else-if="matchingItems.length === 0 && hasSearched" class="status-row item-error">No match '{{ searchText.trim() }}'</div>
        <div
          v-for="(item, index) in matchingItems"
          :key="item[searchIndexSymbol]"
          :class="['dropdown-search-item', { 'item-active': activeItemIndex === index }]"
          @mousedown.prevent="selectItem(item)"
          @mouseenter="activeItemIndex = index"
          @mouseleave="clearActiveItem(index)"
        >
          <div class="dropdown-search-item__primary" :title="displayValue(item, primaryKey)" v-html="highlightValue(item, primaryKey)"></div>
          <div v-if="normalisedSecondaryFields.length" class="dropdown-search-item__secondary">
            <span v-for="field in normalisedSecondaryFields" :key="field.key" class="dropdown-search-item__field">
              <span class="dropdown-search-item__label">{{ field.label }}</span>
              <span class="dropdown-search-item__value" :title="displayValue(item, field.key)" v-html="highlightValue(item, field.key)"></span>
            </span>
          </div>
        </div>
        <div v-if="hasMoreResults" class="status-row">Showing first {{ maximumResults }} of {{ matchingItemsTotal }} matches</div>
      </div>
    </div>
  </div>
</template>

<script>
const searchDelay = 300;

export default {
  name: 'DropdownSearch',
  // Usage:
  // - Local mode: pass `items`; all enumerable item fields are searched by default.
  // - Set `searchKeys` to limit local searching to specific item fields.
  // - Server mode: pass `searchFunction`; it receives the query and normalised search terms.
  props: {
    items: { type: Array, default: function () { return []; } }, // The local items in the dropdown
    primaryKey: { type: String, required: true }, // The item field shown as the primary value
    // Usage: primary-key="VehicleNo"
    secondaryFields: { type: Array, default: function () { return []; } }, // The item fields shown below the primary value
    // Usage: :secondary-fields="['VehicleCode', { key: 'Status', label: 'Status' }]"
    searchKeys: { type: Array, default: function () { return []; } }, // Optional local search fields; defaults to all enumerable item fields
    // Usage: :search-keys="['VehicleNo', 'VehicleCode']"
    searchFunction: { type: Function, default: null }, // Optional server search function, receives (query, searchTerms)
    // Usage: :search-function="searchJobs"; signature: searchJobs(query, searchTerms)
    placeholder: { type: String, default: 'Search' }, // The input placeholder
    selectedObject: { type: Object, default: function () { return null; } }, // The selected item in the dropdown
    search: { type: String, default: '' }, // Get the value of the input
    minimumSearchLength: { type: Number, default: 2 }, // The minimum length for each search term
    // Usage: :minimum-search-length="2"
    maximumResults: { type: Number, default: 50 }, // The maximum number of results displayed
    // Usage: :maximum-results="100"
    isDisabled: { type: Boolean, default: false }, // Set as true to disable the dropdown
    textAlign: { type: String, default: '' }, // Set this to left, center or right

    // Public events
    // @selected(newSelectedObject: Object, oldSelectedObject: Object | null) - fired when an item is selected
    // @focusChanged(focusedState: Boolean) - fired when the input focus changes
    // @keydown(event: KeyboardEvent) - fired when a key is pressed while the input is focused
  },
  data: function () {
    return {
      searchIndexSymbol: Symbol('dropdownServerSearchIndex'),
      searchText: '',
      privateSelectedObject: this.selectedObject,
      inputFocused: false,
      matchingItems: [],
      matchingItemsTotal: 0,
      activeItemIndex: -1,
      searchError: '',
      searching: false,
      hasSearched: false,
      debounceTimeoutId: null,
      localSearchTimeoutId: null,
      searchRequestId: 0,
      positionFrameId: null,
      selectionClearedByInput: false,
      isDestroying: false,
    };
  },
  computed: {
    dropdownIsVisible: function () {
      return this.inputFocused && !this.isDisabled;
    },
    normalisedSecondaryFields: function () {
      const fields = Array.isArray(this.secondaryFields) ? this.secondaryFields : [];
      return fields.map(function (field) {
        if (typeof field === 'string') {
          return { key: field, label: field };
        }
        if (!field || typeof field !== 'object') {
          return null;
        }
        return { key: field.key, label: field.label || field.key };
      }).filter(function (field) {
        return field && field.key && field.key !== this.primaryKey;
      }, this).slice(0, 3);
    },
    effectiveSearchKeys: function () {
      if (Array.isArray(this.searchKeys) && this.searchKeys.length) {
        return this.searchKeys;
      }

      const keys = [];
      const items = Array.isArray(this.items) ? this.items : [];
      items.forEach(function (item) {
        if (!item || typeof item !== 'object') {
          return;
        }
        Object.keys(item).forEach(function (key) {
          if (!keys.includes(key)) {
            keys.push(key);
          }
        });
      });
      return keys.length ? keys : [this.primaryKey];
    },
    canSearch: function () {
      return this.getSearchTerms(this.searchText).length > 0;
    },
    showDefaultLocalResults: function () {
      return !this.searchFunction && !String(this.searchText || '').trim();
    },
    minimumSearchLengthValue: function () {
      const value = Number(this.minimumSearchLength);
      return Number.isFinite(value) && value > 0 ? Math.floor(value) : 1;
    },
    resultLimit: function () {
      const value = Number(this.maximumResults);
      return Number.isFinite(value) && value > 0 ? Math.floor(value) : 50;
    },
    caption: function () {
      if (this.showDefaultLocalResults) {
        return 'Showing ' + this.matchingItems.length + ' of ' + this.matchingItemsTotal + ' match' + (this.matchingItemsTotal === 1 ? '' : 'es');
      }
      if (!this.canSearch) {
        return 'Enter at least ' + this.minimumSearchLengthValue + ' characters';
      }
      if (this.searching) {
        return 'Searching';
      }
      if (this.searchFunction) {
        const resultCount = this.matchingItems.length;
        return resultCount
          ? 'Showing top ' + resultCount + ' result' + (resultCount === 1 ? '' : 's')
          : 'Showing 0 results';
      }
      return 'Showing ' + this.matchingItems.length + ' of ' + this.matchingItemsTotal + ' match' + (this.matchingItemsTotal === 1 ? '' : 'es');
    },
    hasMoreResults: function () {
      return !this.searchFunction && this.matchingItemsTotal > this.matchingItems.length;
    },
  },
  methods: {
    getSearchTerms: function (query) {
      const minimumLength = this.minimumSearchLengthValue;
      return String(query || '').trim().split(/\s+/).filter(function (term) {
        return term.length >= minimumLength;
      });
    },
    clearSearchResults: function () {
      this.matchingItems = [];
      this.matchingItemsTotal = 0;
      this.activeItemIndex = -1;
      this.searchError = '';
      this.searching = false;
      this.hasSearched = false;
    },
    cancelPositionFrame: function () {
      if (this.positionFrameId !== null) {
        cancelAnimationFrame(this.positionFrameId);
        this.positionFrameId = null;
      }
    },
    cancelPendingSearch: function () {
      if (this.debounceTimeoutId !== null) {
        clearTimeout(this.debounceTimeoutId);
        this.debounceTimeoutId = null;
      }
      if (this.localSearchTimeoutId !== null) {
        clearTimeout(this.localSearchTimeoutId);
        this.localSearchTimeoutId = null;
      }
      this.searchRequestId += 1;
    },
    isCurrentSearch: function (requestId) {
      return !this.isDestroying && requestId === this.searchRequestId;
    },
    queuePosition: function () {
      if (this.isDestroying) {
        return;
      }
      this.$nextTick(function () {
        if (!this.isDestroying && this.dropdownIsVisible) {
          this.positionDropdownList();
        }
      });
    },
    clearSelectedObjectFromInput: function (value) {
      if (!this.privateSelectedObject) {
        return;
      }
      const selectedValue = this.displayValue(this.privateSelectedObject, this.primaryKey);
      if (String(value == null ? '' : value) === selectedValue) {
        return;
      }
      this.privateSelectedObject = null;
      this.selectionClearedByInput = true;
      this.$emit('update:selectedObject', null);
    },
    inputFocusChanged: function (focused) {
      this.inputFocused = focused;
      this.$emit('focusChanged', focused);
      if (!focused) {
        this.cancelPositionFrame();
      }
      if (focused) {
        if (!this.searchFunction && !String(this.searchText || '').trim()) {
          this.searchItems('');
        }
        this.$nextTick(function () {
          if (this.isDestroying || !this.dropdownIsVisible) {
            return;
          }
          this.positionDropdownList();
          const selectedValue = this.privateSelectedObject
            ? this.displayValue(this.privateSelectedObject, this.primaryKey)
            : '';
          if (selectedValue && this.searchText === selectedValue && this.$refs.input && this.$refs.input.selectText) {
            this.$refs.input.selectText();
          }
        });
      }
    },
    searchTextInputChanged: function (newValue) {
      this.clearSelectedObjectFromInput(newValue);
      this.$emit('update:search', newValue);
      this.debounceSearch(newValue);
    },
    debounceSearch: function (query) {
      this.cancelPendingSearch();

      if (!this.searchFunction && !String(query || '').trim()) {
        this.searchItems('');
        return;
      }

      this.debounceTimeoutId = setTimeout(function () {
        this.debounceTimeoutId = null;
        this.searchItems(query);
      }.bind(this), searchDelay);
    },
    searchItems: function (query) {
      this.cancelPendingSearch();
      const trimmedQuery = String(query || '').trim();
      const searchTerms = this.getSearchTerms(trimmedQuery);
      this.activeItemIndex = -1;
      this.searchError = '';
      const requestId = this.searchRequestId;

      if (!searchTerms.length) {
        if (this.searchFunction) {
          this.clearSearchResults();
        } else {
          const items = Array.isArray(this.items) ? this.items : [];
          this.matchingItems = items.slice(0, this.resultLimit).map(function (item, index) {
            return Object.assign({}, item, { [this.searchIndexSymbol]: index });
          }, this);
          this.matchingItemsTotal = items.length;
          this.searching = false;
          this.hasSearched = false;
        }
        this.queuePosition();
        return;
      }

      this.hasSearched = true;
      this.searching = true;
      if (this.searchFunction) {
        this.matchingItems = [];
        this.matchingItemsTotal = 0;
        this.searchServerResults(trimmedQuery, searchTerms, requestId);
        return;
      }

      this.localSearchTimeoutId = window.setTimeout(function () {
        this.localSearchTimeoutId = null;
        if (!this.isCurrentSearch(requestId)) {
          return;
        }
        const matchingItems = [];
        let matchingItemsTotal = 0;
        const formattedQueries = searchTerms.map(function (term) { return term.toLowerCase(); });
        const items = Array.isArray(this.items) ? this.items : [];
        for (let index = 0; index < items.length; index += 1) {
          const item = items[index];
          const matches = formattedQueries.some(function (formattedQuery) {
            return this.effectiveSearchKeys.some(function (key) {
              return String(item?.[key] ?? '').toLowerCase().includes(formattedQuery);
            });
          }, this);
          if (matches) {
            matchingItemsTotal += 1;
            if (matchingItems.length < this.resultLimit) {
              matchingItems.push(Object.assign({}, item, { [this.searchIndexSymbol]: matchingItems.length }));
            }
          }
        }
        this.matchingItems = matchingItems;
        this.matchingItemsTotal = matchingItemsTotal;
        this.searching = false;
        this.queuePosition();
      }.bind(this), 0);
    },
    searchServerResults: async function (query, searchTerms, requestId) {
      try {
        const response = await this.searchFunction(query, searchTerms);
        if (!this.isCurrentSearch(requestId)) {
          return;
        }

        const responseItems = Array.isArray(response)
          ? response
          : response && Array.isArray(response.items)
            ? response.items
            : null;
        if (responseItems === null) {
          throw new Error('Invalid search response');
        }
        if (responseItems.length === 1 && String(responseItems[0].Results) === '1' && responseItems[0].ErrMsg) {
          throw new Error(responseItems[0].ErrMsg);
        }
        this.matchingItems = responseItems.slice(0, this.resultLimit).map(function (item, index) {
          return Object.assign({}, item, { [this.searchIndexSymbol]: index });
        }, this);
        this.matchingItemsTotal = this.matchingItems.length;
        this.searching = false;
        this.queuePosition();
      } catch (_error) {
        if (!this.isCurrentSearch(requestId)) {
          return;
        }
        this.clearSearchResults();
        this.searchError = 'Unable to load search results';
        this.queuePosition();
      }
    },
    inputKeyDown: function (keyboardEvent) {
      if (keyboardEvent.key === 'ArrowDown' && this.matchingItems.length) {
        keyboardEvent.preventDefault();
        this.activeItemIndex = Math.min(this.activeItemIndex + 1, this.matchingItems.length - 1);
      } else if (keyboardEvent.key === 'ArrowUp' && this.matchingItems.length) {
        keyboardEvent.preventDefault();
        this.activeItemIndex = Math.max(this.activeItemIndex - 1, 0);
      } else if (keyboardEvent.key === 'Enter' && this.activeItemIndex >= 0) {
        keyboardEvent.preventDefault();
        this.selectItem(this.matchingItems[this.activeItemIndex]);
      } else if (keyboardEvent.key === 'Escape') {
        this.setBlur();
      }
      this.$emit('keydown', keyboardEvent);
    },
    arrowClicked: function (event) {
      event.preventDefault();
      if (this.isDisabled) {
        return;
      }
      if (this.dropdownIsVisible) {
        this.setBlur();
      } else {
        this.setFocus();
      }
    },
    clearSearch: function (event) {
      event.preventDefault();
      this.searchText = '';
      this.clearSelectedObjectFromInput('');
      this.$emit('update:search', '');
      this.searchItems('');
      this.setFocus();
    },
    selectItem: function (item) {
      const selectedItem = Object.assign({}, item);
      delete selectedItem[this.searchIndexSymbol];
      const previousSelectedObject = this.privateSelectedObject;
      this.selectionClearedByInput = false;
      this.cancelPendingSearch();
      this.privateSelectedObject = selectedItem;
      this.searchText = this.displayValue(selectedItem, this.primaryKey);
      this.$emit('update:selectedObject', selectedItem);
      this.$emit('update:search', this.searchText);
      this.$emit('selected', selectedItem, previousSelectedObject);
      this.clearSearchResults();
      this.setBlur();
    },
    clearActiveItem: function (index) {
      if (this.activeItemIndex === index) {
        this.activeItemIndex = -1;
      }
    },
    displayValue: function (item, key) {
      const value = item?.[key];
      return value === null || value === undefined || value === '' ? 'No data' : String(value);
    },
    highlightValue: function (item, key) {
      const value = this.displayValue(item, key);
      const searchTerms = this.getSearchTerms(this.searchText).sort(function (left, right) {
        return right.length - left.length;
      });
      if (!searchTerms.length || value === 'No data') {
        return this.escapeHtml(value);
      }

      const matchPattern = new RegExp(searchTerms.map(function (term) {
        return term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }).join('|'), 'ig');
      let lastIndex = 0;
      let highlightedValue = '';
      value.replace(matchPattern, function (match, offset) {
        highlightedValue += this.escapeHtml(value.slice(lastIndex, offset));
        highlightedValue += '<mark>' + this.escapeHtml(match) + '</mark>';
        lastIndex = offset + match.length;
        return match;
      }.bind(this));
      return highlightedValue + this.escapeHtml(value.slice(lastIndex));
    },
    escapeHtml: function (value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
    getTriggerRect: function () {
      const dropdown = this.$refs.dropdown;
      return dropdown && typeof dropdown.getBoundingClientRect === 'function'
        ? dropdown.getBoundingClientRect()
        : null;
    },
    positionDropdownList: function () {
      const list = this.$refs.dropdownList;
      if (this.isDestroying || !list || !this.dropdownIsVisible) {
        return;
      }
      const rect = this.getTriggerRect();
      if (!rect) {
        return;
      }

      list.style.width = rect.width + 'px';
      const listHeight = list.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const goUpwards = spaceBelow < listHeight && rect.top > spaceBelow;
      const viewportPadding = 4;
      const maxLeft = Math.max(viewportPadding, window.innerWidth - list.offsetWidth - viewportPadding);
      const left = Math.min(Math.max(rect.left, viewportPadding), maxLeft);
      const maxTop = Math.max(viewportPadding, window.innerHeight - listHeight - viewportPadding);
      const top = Math.min(
        Math.max(goUpwards ? rect.top - listHeight : rect.bottom, viewportPadding),
        maxTop,
      );
      list.style.top = top + 'px';
      list.style.left = left + 'px';
      list.classList.toggle('upwards', goUpwards);
    },
    onWindowChanged: function () {
      if (!this.isDestroying && this.dropdownIsVisible && this.positionFrameId === null) {
        this.positionFrameId = requestAnimationFrame(function () {
          this.positionFrameId = null;
          if (!this.isDestroying && this.dropdownIsVisible) {
            this.positionDropdownList();
          }
        }.bind(this));
      }
    },
    setFocus: function () {
      if (!this.isDestroying && this.$refs.input && this.$refs.input.setFocus) {
        this.$refs.input.setFocus();
      }
    },
    setBlur: function () {
      if (!this.isDestroying && this.$refs.input && this.$refs.input.setBlur) {
        this.$refs.input.setBlur();
      }
    },
  },
  watch: {
    items: function () {
      this.searchItems(this.searchText);
    },
    effectiveSearchKeys: function () {
      this.searchItems(this.searchText);
    },
    selectedObject: function (value) {
      const preserveInput = !value && this.selectionClearedByInput;
      if (!preserveInput) {
        this.cancelPendingSearch();
      }
      this.privateSelectedObject = value;
      if (value) {
        this.searchText = this.displayValue(value, this.primaryKey);
      } else if (!preserveInput) {
        this.searchText = '';
        this.$emit('update:search', '');
      }
      this.selectionClearedByInput = false;
      if (!preserveInput || String(this.searchText || '').trim()) {
        this.clearSearchResults();
      }
    },
    search: function (value) {
      if (value !== this.searchText) {
        const nextValue = value || '';
        this.clearSelectedObjectFromInput(nextValue);
        this.searchText = nextValue;
        this.debounceSearch(nextValue);
      }
    },
    dropdownIsVisible: function (visible) {
      if (this.isDestroying) {
        return;
      }
      if (visible) {
        window.addEventListener('scroll', this.onWindowChanged, true);
        window.addEventListener('resize', this.onWindowChanged);
        this.queuePosition();
      } else {
        window.removeEventListener('scroll', this.onWindowChanged, true);
        window.removeEventListener('resize', this.onWindowChanged);
        this.cancelPositionFrame();
      }
    },
  },
  mounted: function () {
    this.privateSelectedObject = this.selectedObject;
    this.searchText = this.selectedObject ? this.displayValue(this.selectedObject, this.primaryKey) : (this.search || '');
    const dropdownList = this.$refs.dropdownList;
    if (dropdownList) {
      document.body.appendChild(dropdownList);
      dropdownList.style.position = 'fixed';
      dropdownList.style.zIndex = '99998';
    }
  },
  beforeDestroy: function () {
    this.isDestroying = true;
    this.cancelPendingSearch();
    const dropdownList = this.$refs.dropdownList;
    if (dropdownList && document.body.contains(dropdownList)) {
      document.body.removeChild(dropdownList);
    }
    window.removeEventListener('scroll', this.onWindowChanged, true);
    window.removeEventListener('resize', this.onWindowChanged);
    this.cancelPositionFrame();
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.dropdown-search {
  width: 100%;
  position: relative;
  height: 25px;
  padding: 0;

  > .dropdown-input {
    width: calc(100% - 10px);
  }

  > .arrow-container {
    position: absolute;
    top: 0;
    right: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    > .arrow-down {
      border-width: 6px 6px 0;
      border-style: solid;
      border-color: rgba(64, 64, 64, 1) transparent transparent;
      border-radius: 10px;
      transition: 0.3s;
    }

    > .arrow-disabled {
      opacity: 0.3;
    }

    > .arrow-flip {
      transform: scaleY(-1);
    }
  }
}
</style>

<style lang="scss">
.dropdown-search-list {
  min-width: 260px;
  max-height: 360px;
  overflow: auto;
  background-color: white;
  box-sizing: border-box;
  border: 1px solid rgb(150, 150, 150);
  border-radius: 0 0 10px 10px;
  box-shadow: 0 2px 8px -4px rgba(0, 0, 0, 0.25);
  font-size: 12px;

  &.upwards {
    border-radius: 10px 10px 0 0;
  }

  > .filtered-caption {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 25px;
    padding: 5px 10px;
    background-color: white;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    box-sizing: border-box;
    font-size: 10px;

    > img {
      margin-left: 5px;
      cursor: pointer;

      &:hover {
        background-color: rgba(200, 200, 200, 0.5);
      }
    }
  }

  .dropdown-search-item {
    display: block;
    min-height: 52px;
    box-sizing: border-box;
    padding: 5px 10px;
    overflow: hidden;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    background-color: white;
    color: rgba(0, 0, 0, 0.85);
    cursor: pointer;

    &:hover,
    &.item-active {
      background-color: rgba(0, 0, 0, 0.05);
      color: rgba(0, 0, 0, 0.85);
    }

    &__primary {
      min-width: 0;
      overflow: hidden;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &__secondary {
      display: flex;
      flex-wrap: wrap;
      min-width: 0;
      min-height: 24px;
      gap: 12px;
      overflow: hidden;
      margin-top: 3px;
      padding: 0;
    }

    &__field {
      display: flex;
      flex: 0 1 auto;
      flex-direction: column;
      min-width: 0;
      max-width: 300px;
      overflow: hidden;
      padding: 0 4px 0 0;
      box-sizing: border-box;
    }

    &__label {
      flex: 0 0 auto;
      color: rgba(0, 0, 0, 0.72);
      font-size: 9px;
      font-weight: 600;
      line-height: 10px;
      white-space: nowrap;
    }

    &__value {
      min-width: 0;
      overflow: hidden;
      overflow-wrap: anywhere;
      color: rgba(0, 0, 0, 0.75);
      font-size: 11px;
      line-height: 14px;
      white-space: normal;
    }

    mark {
      padding: 0 1px;
      background-color: rgba(250, 204, 21, 0.45);
      color: inherit;
    }
  }

  .status-row {
    margin: 1px;
    padding: 5px 7px;
    color: rgba(0, 0, 0, 0.7);
  }

  .item-error {
    background-color: rgba(255, 0, 0, 0.2);
  }

}
</style>
